import each from 'lodash/each';
import find from 'lodash/find';
import get from 'lodash/get';
import intersectionBy from 'lodash/intersectionBy';
import moment from 'moment';
import reduce from 'lodash/reduce';

import { AppSettingsModule } from 'services/settings.provider';
import { CategoryServiceModule } from 'services/data/categories.service';
import { DataServiceModule } from 'services/data/data.service';
import { SearchServiceModule } from 'services/data/search.service';
import { SegmentServiceModule } from 'services/tracking/segment/segment.service';

import { DiscountsAnalyticsServiceModule } from './discounts-analytics.service';

export const DiscountsServiceModule = angular
    .module('app.services.data.discountsService', [
        DataServiceModule,
        AppSettingsModule,
        SearchServiceModule,
        CategoryServiceModule,
        DiscountsAnalyticsServiceModule,
        SegmentServiceModule
    ]).service('discountsService', (
        $q,
        dataService,
        settings,
        searchService,
        categoryService,
        $window,
        $filter,
        discountsAnalyticsService
    ) => {
        return {
            create,
            getDiscount,
            getDiscounts,
            getDiscountDuration,
            getDiscountMetricsFromOrders,
            getDiscountStatus,
            getMetrics,
            getOrderMetricsForDiscount,
            getScopeDataForDiscount,
            getUsesLeft,
            isCodeTaken,
            removeDiscount,
            save,
            search,
            trackSegmentEvent,
            updateDiscountCodeCounts,
            getOrdersByDiscountCode,
            getOrdersByDiscountCodes,
            transformDiscount
        };

        function create(discount) {
            return dataService.create(settings.api.discounts, discount).then((response) => {
                const savedDiscount = transformDiscount(response);
                discountsAnalyticsService.sendDiscountCreateEvent(savedDiscount);
                return savedDiscount;
            });
        }

        function save(discount) {
            return dataService.save(settings.api.discounts, discount.id, discount).then((response) => {
                const savedDiscount = transformDiscount(response);
                discountsAnalyticsService.sendDiscountUpdateEvent(savedDiscount);
                return savedDiscount;
            });
        }

        function getDiscounts(pageSize = null, page = 1, queryFilter = '', sortBy) {
            let startIndex = 0;
            if (pageSize) {
                startIndex = ((page || 1) - 1) * pageSize;
            }
            return dataService.getAll(settings.api.discounts, {
                startIndex,
                pageSize,
                filter: queryFilter,
                sortBy
            }).then((discounts) => {
                discounts.items = discounts.items.map(transformDiscount);
                return discounts;
            });
        }

        function search(searchString = '', sortBy, pageSize = 25, page = 0) {
            return dataService.getAll(settings.api.discounts, {
                startIndex: page * pageSize,
                pageSize,
                search: searchString,
                sortBy
            }).then((discounts) => {
                discounts.items = discounts.items.map(transformDiscount);
                return discounts;
            });
        }

        function getDiscountMetricsFromOrders() {
            return dataService.getAll(settings.api.discountMetrics).then((discountMetrics) => {
                return discountMetrics;
            });
        }

        function transformDiscount(discount) {
            discount.status = getDiscountStatus(discount);
            discount.duration = getDiscountDuration(discount);
            discount.usesLeft = getUsesLeft(discount);
            discount.discountValue = discount.discountType === 'percent' ?
                `${discount.percentagePriceReduction}%` :
                `${$filter('currency')(discount.fixedPriceReduction)}`;
            return discount;
        }

        function getDiscount(id) {
            return dataService.get(settings.api.discounts, id).then((response) => {
                return transformDiscount(response);
            });
        }

        function getDiscountDuration(discount) {
            let duration;

            if (discount.hasStartDateTime && discount.hasEndDateTime) {
                let diff = moment(discount.endDateTime).diff(discount.startDateTime, 'days');
                duration = `${diff} day${diff === 1 ? '' : 's'}`;
            } else {
                duration = 'Unlimited';
            }

            return duration;
        }

        function discountIsReady(discount, today) {
            let hasNotReachedStartDate = discount.hasStartDateTime && discount.hasEndDateTime
                                            && today.isBefore(discount.startDateTime);
            return discount.isActive && hasNotReachedStartDate;
        }

        function discountIsLive(discount, today) {
            return discount.isActive && discountUsageIsLive(discount) && discountDurationIsLive(discount, today);
        }

        function discountUsageIsLive(discount) {
            let hasUsesLeft = discount.usageLimit - discount.currentUsageCount > 0;
            return (!discount.unlimited && hasUsesLeft) || discount.unlimited;
        }

        function discountDurationIsLive(discount, today) {
            let hasUnlimitedDuration = (discount.unlimitedDuration
                                    || (!discount.hasStartDateTime && !discount.hasEndDateTime));
            let todayIsWithinDateRange = (discount.hasStartDateTime && discount.hasEndDateTime
                                    && today.isBetween(discount.startDateTime, discount.endDateTime))
                                    || today.isSame(discount.startDateTime);
            return hasUnlimitedDuration || todayIsWithinDateRange;
        }

        function discountHasEnded(discount, today) {
            let dateExpired = today.isAfter(discount.endDateTime);
            let usageLimitExpired = !discount.unlimited && discount.usageLimit - discount.currentUsageCount <= 0;
            return discount.isActive && (dateExpired || usageLimitExpired);
        }

        function getDiscountStatus(discount) {
            let status = {};
            let statusDesc = 'This discount ';
            let today = moment();

            if (!discount.isActive) {
                statusDesc += 'is disabled';
                status = {
                    code: 'disabled',
                    text: 'Disabled'
                };
            } else if (discountIsReady(discount, today)) {
                let daysUntilStart = today.to(discount.startDateTime);
                statusDesc += `will start ${daysUntilStart}`;
                if (!discount.unlimited) {
                    statusDesc += `, and can be used ${discount.usageLimit} time${discount.usageLimit > 1 ? 's' : ''}`;
                }
                status = {
                    code: 'ready',
                    text: 'Ready'
                };
            } else if (discountIsLive(discount, today)) {
                let daysUntilEnd = today.to(discount.endDateTime);
                let diff = discount.usageLimit - discount.currentUsageCount;
                let remainingUses = diff < 0 ? 0 : diff;

                statusDesc += 'is live';
                if (!discount.unlimitedDuration && discount.unlimited) statusDesc += ', and';
                else if (discount.unlimitedDuration && !discount.unlimited) statusDesc += '';
                else if (!discount.unlimitedDuration && !discount.unlimited) statusDesc += ', ';

                if (!discount.unlimitedDuration) statusDesc += ` will end ${daysUntilEnd}`;
                if (!discount.unlimited) {
                    statusDesc += `, and can be used ${remainingUses} more time${remainingUses > 1 ? 's' : ''}`;
                }
                status = {
                    code: 'live',
                    text: 'Live'
                };
            } else if (discountHasEnded(discount, today)) {
                let daysFromEnd = today.to(discount.endDateTime);
                statusDesc += discount.unlimitedDuration ? 'reached its usage limit' : `ended ${daysFromEnd}`;
                status = {
                    code: 'ended',
                    text: 'Ended'
                };
            }

            status.desc = statusDesc;

            return status;
        }


        function isCodeTaken(code) {
            return getDiscounts().then((discounts) => {
                return angular.isDefined(find(discounts.items, { code: code }));
            });
        }

        function removeDiscount(discount) {
            return dataService.remove(settings.api.discounts, discount.id);
        }

        function getUsesLeft(discount) {
            if (discount.unlimited) {
                return 'Unlimited';
            } else if (discount.usageLimit > discount.currentUsageCount) {
                return discount.usageLimit - discount.currentUsageCount;
            }
            return 'None remaining';
        }

        function getMetrics() {
            return Promise.all([
                getDiscounts(1, 1),
                getDiscounts(1, 1, { isActive: true }),
                getDiscounts(1, 1, { isActive: false }),
                getDiscountMetricsFromOrders()
            ]).then(([allDiscountsMetrics, activeMetrics, inactiveMetrics, orders]) => {
                const totalAmountSaved = get(orders, '[0].totalDiscountAmount', 0) / 100;
                const totalDiscountUses = get(orders, '[0].noOfDiscounts', 0);
                return {
                    totalAmountSaved,
                    totalDiscountUses,
                    activeCount: activeMetrics.total,
                    inactiveCount: inactiveMetrics.total,
                    totalCount: allDiscountsMetrics.total
                };
            }).catch(() => {
                return {
                    activeCount: 0,
                    inactiveCount: 0,
                    totalCount: 0,
                    totalDiscountUses: 0,
                    totalAmountSaved: 0
                };
            });
        }

        function getOrderMetricsForDiscount(discountCode) {
            return getOrdersByDiscountCode(discountCode)
                .then((orders) => {
                    return {
                        code: discountCode,
                        orderCount: orders.length,
                        totalOrderAmount: reduce(orders, (total, order) => total + order.grandTotal || 0, 0),
                        totalDiscountAmount: reduce(orders, (total, order) => total + order.cart.discountAmount || 0, 0)
                    };
                });
        }

        /* eslint-disable no-useless-escape */
        function getOrdersByDiscountCode(discountCode) {
            const searchOptions = {
                facets: '*',
                filters:
                    `cart.discount.code:\"${discountCode}\" AND NOT status:\"Canceled\" AND NOT status:\"Refunded\"`
            };
            return searchService
                .search('orders', '', searchOptions, false)
                .then(response => response.hits);
        }
        /* eslint-enable no-useless-escape */

        /* eslint-disable no-useless-escape */
        function getOrdersByDiscountCodes(discountCodesList) {
            if (!angular.isArray(discountCodesList) || discountCodesList.length === 0) {
                return $q.reject('Pass in an array of discount codes');
            }
            const discountCodeFacetFilter =
                discountCodesList.map(discountCode => `cart.discount.code:\"${discountCode}\"`).join(' OR ');

            const searchOptions = {
                facets: '*',
                filters: `(${discountCodeFacetFilter}) AND NOT status:\"Canceled\" AND NOT status:\"Refunded\"`
            };
            return searchService.search('orders', '', searchOptions, false).then(response => response.hits);
        }
        /* eslint-enable no-useless-escape */

        function updateDiscountCodeCounts() {
            return getMetrics();
        }

        function trackSegmentEvent() {
            return $q.resolve(true);
        }

        function getScopeDataForDiscount(discount) {
            let promises = [];
            let selectedProductsCount = 0;
            if (discount.scope && discount.scope.items && discount.scope.items.length > 0) {
                let selectedProductIds = discount.scope.items
                    .filter(item => item.type === 'product')
                    .map(item => item.id);
                let selectedCategoryIds = discount.scope.items
                    .filter(item => item.type === 'category')
                    .map((item) => { return { id: item.id }; });
                if (angular.isArray(selectedProductIds) && selectedProductIds.length > 0) {
                    promises.push(
                        searchService.getManyByIds('products', selectedProductIds)
                            .then(({ results: products }) => {
                                selectedProductsCount += products.length;
                                each(discount.scope.items, (item) => {
                                    if (item.type === 'product') {
                                        item.data = find(products, { id: item.id });
                                    }
                                });
                                return products;
                            })
                    );
                }
                if (angular.isArray(selectedCategoryIds) && selectedCategoryIds.length > 0) {
                    promises.push(categoryService.getCategoryTree()
                        .then(categoryService.flattenCategories)
                        .then((categories) => {
                            let selectedCategories = intersectionBy(categories, selectedCategoryIds, 'id');
                            let category;
                            each(discount.scope.items, (item) => {
                                if (item.type === 'category') {
                                    category = find(selectedCategories, { id: item.id });
                                    item.data = category;
                                    selectedProductsCount += category.productCount;
                                }
                            });
                            return selectedCategories;
                        }));
                }
                return $q.all(promises).then(() => {
                    return {
                        discount,
                        selectedProductsCount
                    };
                });
            }
            return $q.resolve({
                discount,
                selectedProductsCount: 0
            });
        }
    }).name;



// WEBPACK FOOTER //
// ./src/app/services/data/discounts.service.js